import { causeLabel } from "@/lib/mortalityCauses";
import { displayDate } from "@/lib/utils";
import type { DailyReportData } from "./DailyReportPage";
import { hasAnyMeasure } from "./StepMeasurements";
import { ADHOC_BUYER } from "./constants";
import { paidLines, saleTotal } from "./paidNow";
import type { PaymentDraft } from "@/hooks/useSalePayments";
import type { FeedFormula } from "@/types/models";

interface Props {
  data: DailyReportData;
  formulas: FeedFormula[];
  isManager?: boolean;
  /** True when the day's existing measurements could not be read. "Nothing
   *  measured today" is then a claim about the farm standing in for a network
   *  error — the same substitution this screen's siblings were fixed for. */
  measuresLoadFailed?: boolean;
}

export default function StepReview({
  data,
  formulas,
  isManager,
  measuresLoadFailed,
}: Props) {
  const formulaName = (id: string) =>
    formulas.find((f) => f.id === parseInt(id))?.name || "-";

  const materialsWithQty = data.materials.filter((m) => parseFloat(m.qty) > 0);

  // Only flocks that were actually measured, and nothing at all when the day's
  // readings could not be READ. Both conditions mirror the submit filter, which
  // is the point: the review must show what will be SENT, so the two must be
  // changed together.
  const measuredFlocks = measuresLoadFailed ? [] : data.flocks.filter(hasAnyMeasure);
  // A blank measure renders as an em dash, never as 0. The whole point of the
  // nullable columns behind this is that "not measured" and "measured as zero"
  // stay different, and a review screen that prints 0 for both undoes it.
  const measure = (v: string) => (v.trim() === "" ? "—" : v);

  const salesTotal = (rows: Array<{ total_amount: string }>) =>
    rows.reduce((sum, r) => sum + (parseFloat(r.total_amount) || 0), 0);

  // Paid at the time of sale: a walk-in pays the whole sale, a buyer on the
  // books what was entered as paid now (W2-6). The rest is on credit.
  const paidTotal = (rows: Array<{ buyer_id: string; total_amount: string; payments?: PaymentDraft[] }>) =>
    rows.reduce(
      (sum, r) =>
        sum +
        (r.buyer_id === ADHOC_BUYER
          ? saleTotal(r)
          : paidLines(r).reduce((s, l) => s + l.amount, 0)),
      0,
    );
  const fmt = (n: number) => n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const eggsTotal = salesTotal(data.sales.eggs);
  const birdsTotal = salesTotal(data.sales.birds);
  const manureTotal = salesTotal(data.sales.manure);
  const bagsTotal = salesTotal(data.sales.bags);
  const totalSalesCount =
    data.sales.eggs.length +
    data.sales.birds.length +
    data.sales.manure.length +
    data.sales.bags.length;
  const totalSalesAmount = eggsTotal + birdsTotal + manureTotal + bagsTotal;

  return (
    <div>
      <h4 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
        Review & Submit
      </h4>

      <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
        Date: <span className="font-medium text-gray-800 dark:text-white/90">{displayDate(data.date)}</span>
      </div>

      {/* Flock Data Table */}
      <div className="mb-4">
        <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Flock Data
        </h5>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Flock</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Mortality</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Egg Trays</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Broken (eggs)</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Feed (kg)</th>
              </tr>
            </thead>
            <tbody>
              {data.flocks.map((f) => (
                <tr key={f.flock_id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                    {f.batch_name}
                    <span className="ml-1 text-[10px] text-gray-400">{f.shed_code}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {f.mortality || "0"}
                    {parseInt(f.mortality) > 0 && f.mortality_cause && (
                      <span className="ml-1 text-[10px] text-gray-400">{causeLabel(f.mortality_cause)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{f.shed_type === "layer" ? (f.egg_trays || "-") : "-"}</td>
                  <td className="px-3 py-2 text-right">{f.shed_type === "layer" ? (f.broken_trays || "0") : "-"}</td>
                  <td className="px-3 py-2 text-right">{f.feed_consumed_kg || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Measurements */}
      <div className="mb-4">
        <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Measurements
        </h5>
        {measuresLoadFailed ? (
          <p className="text-xs text-warning-600 dark:text-warning-400">
            Measurements couldn't be loaded for this date and won't be saved.
            Everything else below will submit normally.
          </p>
        ) : measuredFlocks.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nothing measured today.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Flock</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Body wt (g)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Egg wt (g)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Water (L)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Light (h)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Temp (°C)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Humidity (%)</th>
                </tr>
              </thead>
              <tbody>
                {measuredFlocks.map((f) => (
                  <tr key={f.flock_id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                      {f.batch_name}
                      <span className="ml-1 text-[10px] text-gray-400">{f.shed_code}</span>
                    </td>
                    <td className="px-3 py-2 text-right">{measure(f.m_body_weight_g)}</td>
                    {/* Not gated on shed_type. The submit sends this field for
                        every flock — a brooder shed just has no box to type it
                        in — so gating the review on the form's layout would
                        show a dash for a value that is about to be written. */}
                    <td className="px-3 py-2 text-right">{measure(f.m_egg_weight_g)}</td>
                    <td className="px-3 py-2 text-right">{measure(f.m_water_litres)}</td>
                    <td className="px-3 py-2 text-right">{measure(f.m_light_hours)}</td>
                    <td className="px-3 py-2 text-right">
                      {f.m_temp_min_c.trim() === "" && f.m_temp_max_c.trim() === ""
                        ? "—"
                        : `${measure(f.m_temp_min_c)} / ${measure(f.m_temp_max_c)}`}
                    </td>
                    <td className="px-3 py-2 text-right">{measure(f.m_humidity_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feed Prepared Table */}
      <div className="mb-4">
        <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Feed Prepared
        </h5>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Flock</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Prepared (kg)</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Formula</th>
              </tr>
            </thead>
            <tbody>
              {data.flocks.map((f) => (
                <tr key={f.flock_id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">{f.batch_name}</td>
                  <td className="px-3 py-2 text-right">{f.feed_prepared_kg || "-"}</td>
                  <td className="px-3 py-2">{f.formula_id ? formulaName(f.formula_id) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Raw Materials */}
      {materialsWithQty.length > 0 && (
        <div>
          <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Raw Materials Received
          </h5>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Material</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Quantity</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Unit</th>
                </tr>
              </thead>
              <tbody>
                {materialsWithQty.map((m) => (
                  <tr key={m.raw_material_id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">{m.name}</td>
                    <td className="px-3 py-2 text-right">{m.qty}</td>
                    <td className="px-3 py-2">{m.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {materialsWithQty.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No raw materials added in this report.
        </p>
      )}

      {/* Sales (manager only) */}
      {isManager && (
        <div className="mt-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Sales
          </h5>
          {totalSalesCount === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No sales to record today.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Type</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Rows</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Amount (₹)</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Paid now (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales.eggs.length > 0 && (
                    <tr className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">Eggs</td>
                      <td className="px-3 py-2 text-right">{data.sales.eggs.length}</td>
                      <td className="px-3 py-2 text-right">{eggsTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right">{fmt(paidTotal(data.sales.eggs))}</td>
                    </tr>
                  )}
                  {data.sales.birds.length > 0 && (
                    <tr className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">Birds</td>
                      <td className="px-3 py-2 text-right">{data.sales.birds.length}</td>
                      <td className="px-3 py-2 text-right">{birdsTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right">{fmt(paidTotal(data.sales.birds))}</td>
                    </tr>
                  )}
                  {data.sales.manure.length > 0 && (
                    <tr className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">Manure</td>
                      <td className="px-3 py-2 text-right">{data.sales.manure.length}</td>
                      <td className="px-3 py-2 text-right">{manureTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right">{fmt(paidTotal(data.sales.manure))}</td>
                    </tr>
                  )}
                  {data.sales.bags.length > 0 && (
                    <tr className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">Bags</td>
                      <td className="px-3 py-2 text-right">{data.sales.bags.length}</td>
                      <td className="px-3 py-2 text-right">{bagsTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right">{fmt(paidTotal(data.sales.bags))}</td>
                    </tr>
                  )}
                  <tr className="border-t border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50">
                    <td className="px-3 py-2 font-semibold text-gray-800 dark:text-white/90">Total</td>
                    <td className="px-3 py-2 text-right font-semibold">{totalSalesCount}</td>
                    <td className="px-3 py-2 text-right font-semibold">{totalSalesAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {fmt(
                        paidTotal(data.sales.eggs) + paidTotal(data.sales.birds) +
                          paidTotal(data.sales.manure) + paidTotal(data.sales.bags),
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
